import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { fetchOpenAIText } from '../utils/openaiClient';
import { getDictionary, addWord } from '../utils/dictionaryService';
import { supabase } from '../utils/supabaseClient';
import { useSaveToast } from '../hooks/useSaveToast';

export default function DictionaryModal({ isOpen, onClose, studentId }) {
  // Reuses the same toast styling/behavior as the lesson "Saved
  // successfully" notification (.lesson-save-toast, defined in
  // LessonWorkspace.css — this modal only ever renders inside
  // LessonWorkspace, so those styles are already loaded).
  const { saveToast: wordToast, showLessonSaveToast: showWordToast, dismissLessonSaveToast: dismissWordToast } = useSaveToast();
  const [activeTab, setActiveTab] = useState('myWords');
  const [dictionaryEntries, setDictionaryEntries] = useState([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [resolvedStudentId, setResolvedStudentId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchWord, setSearchWord] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Slug → UUID normalization (studentId may be a slug in LessonWorkspace)
  const resolveStudentUuid = useCallback(async (id) => {
    if (!id) return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isUUID) return id;
    try {
      const { data } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', id)
        .maybeSingle();
      return data?.id || null;
    } catch {
      return null;
    }
  }, []);

  const loadDictionary = useCallback(async (uuid) => {
    if (!uuid) { setDictionaryEntries([]); return; }
    setIsLoadingEntries(true);
    try {
      const entries = await getDictionary(uuid);
      setDictionaryEntries(Array.isArray(entries) ? entries : []);
    } catch {
      setDictionaryEntries([]);
    } finally {
      setIsLoadingEntries(false);
    }
  }, []);

  // Resolve UUID once when studentId changes
  useEffect(() => {
    resolveStudentUuid(studentId).then(uuid => {
      setResolvedStudentId(uuid);
    });
  }, [studentId, resolveStudentUuid]);

  // Load when modal opens or resolved ID changes
  useEffect(() => {
    if (isOpen && resolvedStudentId) {
      loadDictionary(resolvedStudentId);
    }
    if (!isOpen) setDictionaryEntries([]);
  }, [isOpen, resolvedStudentId, loadDictionary]);

  // Reload when another component adds a word
  useEffect(() => {
    const handler = () => { if (isOpen && resolvedStudentId) loadDictionary(resolvedStudentId); };
    window.addEventListener('dictionaryUpdated', handler);
    return () => window.removeEventListener('dictionaryUpdated', handler);
  }, [isOpen, resolvedStudentId, loadDictionary]);

  // Поиск слова
  const handleSearchWord = async () => {
    const word = searchWord.trim();
    if (!word) return;

    setIsSearching(true);
    setSearchResult(null);
    showWordToast('info', 'Looking up word variants…', 6000);

    try {
      const prompt = `For the English word or phrase "${word}":
1. Determine if it is a phrasal verb, idiom, or regular word/phrase
2. Determine its CEFR level (A1, A2, B1, B2, C1, or C2) based on word frequency dictionaries
3. Provide its Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
4. Create a natural English example sentence (8-15 words)
5. Determine its part of speech (noun, verb, adjective, adverb, etc.)

Respond ONLY with valid JSON in this format: {"type": "regular/phrasal verb/idiom", "level": "X", "translation": "Y", "example": "Z", "partOfSpeech": "W"}`;

      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      
      // Parse JSON from response
      let level = null;
      let translation = null;
      let example = null;
      
      try {
        let jsonStr = contentStr;
        if (jsonStr.startsWith('```json')) {
          jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
        } else if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
        }
        
        const match = jsonStr.match(/\{[\s\S]*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          
          if (parsed.level && /^[ABC]\d$/.test(parsed.level)) {
            level = parsed.level;
          }
          
          if (parsed.translation) {
            let translationText = parsed.translation.trim();
            if (Array.isArray(parsed.translation)) {
              translationText = parsed.translation
                .map(t => typeof t === 'string' ? t.trim() : String(t).trim())
                .filter(t => t.length > 0)
                .join(', ');
            } else if (typeof translationText === 'string') {
              translationText = translationText
                .replace(/\s+/g, ' ')
                .replace(/,\s*,/g, ',')
                .trim();
            }
            if (translationText && translationText.length > 0) {
              translation = translationText;
            }
          }
          
          if (parsed.example && parsed.example.trim()) {
            example = parsed.example.trim();
          }
        }
      } catch (parseErr) {
        console.error('Error parsing JSON:', parseErr);
      }
      
      setSearchResult({
        word: word,
        level: level,
        translation: translation,
        example: example
      });
    } catch (error) {
      console.error('Error searching word:', error);
      setSearchResult({
        word: word,
        level: null,
        translation: null,
        example: null
      });
    } finally {
      setIsSearching(false);
      dismissWordToast();
    }
  };

  // Добавление найденного слова в словарик через Supabase
  const handleAddSearchedWord = async () => {
    if (!searchResult || !resolvedStudentId) return;
    try {
      await addWord(resolvedStudentId, {
        word: searchResult.word,
        translation: searchResult.translation,
        level: searchResult.level,
        example: searchResult.example,
      });
      await loadDictionary(resolvedStudentId);
      window.dispatchEvent(new CustomEvent('dictionaryUpdated', { detail: { studentId: resolvedStudentId } }));
      showWordToast('success', `Added "${searchResult.word}" to dictionary!`, 1800);
    } catch (e) {
      console.error('Error adding word:', e);
      showWordToast('error', 'Could not add word. Try again.', 3000);
    }
    setActiveTab('myWords');
    setSearchWord('');
    setSearchResult(null);
  };

  // Удаление слова из словарика
  const handleDeleteWord = (id) => {
    setDictionaryEntries(prev => prev.filter(entry => entry.id !== id));
  };

  // Произношение слова
  const handlePronunciation = (word) => {
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = 'en-US';
    speechSynthesis.speak(utterance);
  };

  // Фильтрация и сортировка слов студента (новые сначала)
  const filteredEntries = dictionaryEntries
    .filter(entry => {
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      const haystack = [entry.word, entry.translation, entry.example]
        .map(v => (v || '').toLowerCase());
      return haystack.some(s => s.includes(q));
    })
    .sort((a, b) => {
      // Сортируем по id в порядке убывания (новые слова первыми)
      // id обычно создается через Date.now(), так что большее число = новее
      return (b.id || 0) - (a.id || 0);
    });

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes fadeInAI {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      {/* Overlay для закрытия */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483645,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 200,
          zIndex: 2147483648,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 4px 24px #8a2be244',
          padding: 12,
          minWidth: 280,
          maxWidth: 280,
          width: 450,
          border: '1.5px solid #cfcfff',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          alignItems: 'stretch',
          animation: 'fadeInAI 0.28s cubic-bezier(.4,0,.2,1)',
          transition: 'opacity 0.28s cubic-bezier(.4,0,.2,1), transform 0.28s cubic-bezier(.4,0,.2,1)',
          maxHeight: '70vh',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div
          style={{
            padding: '8px 0 8px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '16px', color: '#333', fontWeight: 600 }}>Dictionary</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background 0.18s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          >
            ×
          </button>
        </div>

        <div style={{ height: 0.5, background: '#ececec', width: '100%', margin: '4px 0' }} />

        {/* Вкладки */}
        <div
          style={{
            display: 'flex',
            gap: 4,
            marginBottom: 8,
          }}
        >
          <button
            onClick={() => {
              setActiveTab('myWords');
              setSearchWord('');
              setSearchResult(null);
            }}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: 'none',
              background: activeTab === 'myWords' ? '#ede2f9' : '#fff',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: activeTab === 'myWords' ? 500 : 400,
              fontSize: '14px',
              color: '#222',
              transition: 'background 0.22s',
            }}
            onMouseEnter={(e) => activeTab !== 'myWords' && (e.currentTarget.style.background = '#f5f0ff')}
            onMouseLeave={(e) => activeTab !== 'myWords' && (e.currentTarget.style.background = '#fff')}
          >
            My Words
          </button>
          <button
            onClick={() => {
              setActiveTab('search');
              setSearchTerm('');
            }}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: 'none',
              background: activeTab === 'search' ? '#ede2f9' : '#fff',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: activeTab === 'search' ? 500 : 400,
              fontSize: '14px',
              color: '#222',
              transition: 'background 0.22s',
            }}
            onMouseEnter={(e) => activeTab !== 'search' && (e.currentTarget.style.background = '#f5f0ff')}
            onMouseLeave={(e) => activeTab !== 'search' && (e.currentTarget.style.background = '#fff')}
          >
            Search Words
          </button>
        </div>

        {/* Контент */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px 0',
            maxHeight: 'calc(70vh - 120px)',
          }}
        >
          {activeTab === 'myWords' ? (
            <>
              {/* Поиск в словах студента */}
              <div style={{ marginBottom: '12px', padding: '0 4px' }}>
                <input
                  type="text"
                  placeholder="Search in my words..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Список слов студента */}
              {isLoadingEntries ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>Loading…</div>
              ) : filteredEntries.length > 0 ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px', 
                  padding: '0 4px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}>
                  {filteredEntries.map((entry) => (
                    <div
                      key={entry.id}
                      style={{
                        padding: '12px',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#222' }}>{entry.word}</h3>
                            <button
                              onClick={() => handlePronunciation(entry.word)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: '2px',
                                borderRadius: '4px',
                                transition: 'background 0.18s',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              title="Pronounce"
                            >
                              🔊
                            </button>
                          </div>
                          <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                            <strong>Translation:</strong> {entry.translation || 'N/A'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                            <strong>Level:</strong> {entry.level || 'N/A'}
                          </div>
                          {entry.example && (
                            <div style={{ fontSize: '12px', color: '#555', fontStyle: 'italic', marginTop: '6px' }}>
                              <strong>Example:</strong> {entry.example}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteWord(entry.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#dc3545',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            transition: 'background 0.18s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#ffe0e0'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '14px' }}>
                  {searchTerm ? 'No words found' : 'No words in your dictionary yet'}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Поиск новых слов */}
              <div style={{ marginBottom: '12px', padding: '0 4px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter a word to search..."
                    value={searchWord}
                    onChange={(e) => setSearchWord(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchWord()}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={handleSearchWord}
                    disabled={isSearching || !searchWord.trim()}
                    style={{
                      padding: '10px 16px',
                      background: isSearching || !searchWord.trim() ? '#ccc' : '#8a2be2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSearching || !searchWord.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: 500,
                      fontSize: '14px',
                      transition: 'background 0.22s',
                    }}
                    onMouseEnter={(e) => !isSearching && searchWord.trim() && (e.currentTarget.style.background = '#7a1be2')}
                    onMouseLeave={(e) => !isSearching && searchWord.trim() && (e.currentTarget.style.background = '#8a2be2')}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Результат поиска */}
              {searchResult && (
                <div style={{ padding: '0 4px' }}>
                  <div
                    style={{
                      padding: '12px',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#222' }}>{searchResult.word}</h3>
                          <button
                            onClick={() => handlePronunciation(searchResult.word)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              padding: '2px',
                              borderRadius: '4px',
                              transition: 'background 0.18s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            title="Pronounce"
                          >
                            🔊
                          </button>
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                          <strong>Translation:</strong> {searchResult.translation}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                          <strong>Level:</strong> {searchResult.level}
                        </div>
                        {searchResult.example && (
                          <div style={{ fontSize: '12px', color: '#555', fontStyle: 'italic', marginTop: '6px' }}>
                            <strong>Example:</strong> {searchResult.example}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleAddSearchedWord}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#8a2be2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '14px',
                      transition: 'background 0.22s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#7a1be2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#8a2be2'}
                  >
                    Add to My Dictionary
                  </button>
                </div>
              )}

              {!searchResult && !isSearching && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '14px' }}>
                  Enter a word above to search for its definition
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {wordToast && createPortal(
        <div
          className={`lesson-save-toast lesson-save-toast--${wordToast.variant}`}
          role="status"
          aria-live="polite"
        >
          <div className="lesson-save-toast-inner">
            <span className="lesson-save-toast-icon" aria-hidden>
              {wordToast.variant === 'success' ? '✓' : 'ℹ'}
            </span>
            <p className="lesson-save-toast-message">{wordToast.message}</p>
            <button
              type="button"
              className="lesson-save-toast-close"
              onClick={dismissWordToast}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

